import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { debounce } from "~/utils/helpers";

import { useHookableRef } from "./useHookableRef";

import type { MutableRefObject } from "react";

/**
 * Used to measure an element's DOMRect.
 */
export function useRect<T extends Element>(
  enabled = true,
): [MutableRefObject<T | null>, DOMRect] {
  const rerender = useReducer(() => ({}), [])[1];

  const [element, setElement] = useState<T | null>(null);
  const elementRef = useHookableRef<T | null>(null, (v) => {
    setElement(v);
    return v;
  });

  const rectRef = useRef<DOMRect>({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    toJSON() {
      throw Error("Not Implemented");
    },
  });

  const measure = useCallback(() => {
    if (element) {
      rectRef.current = element.getBoundingClientRect();
    }
  }, [element]);

  if (!rectRef.current) {
    measure();
  }

  useEffect(() => {
    if (!element || !enabled) {
      return;
    }

    const cb = debounce(() => {
      measure();
      rerender();
    }, 250);

    document.addEventListener("scroll", cb, true);

    return () => {
      document.removeEventListener("scroll", cb, true);
    };
  }, [element, enabled, measure, rerender]);

  useEffect(() => {
    if (!element || !enabled) {
      return;
    }

    measure();
    rerender();

    const observer = new ResizeObserver(() => {
      measure();
      rerender();
    });

    observer.observe(element as Element);

    return () => {
      observer.unobserve(element as Element);
    };
  }, [element, enabled, measure, rerender]);

  return [elementRef, rectRef.current];
}
